import { Formik, Form } from "formik";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import TextBox from "../../../../controls/form/TextBox";
import Forms from "../../../../../services/forms";
import CheckBox from "../../../../controls/form/Checkbox";
import Dropdown from "../../../../controls/form/Dropdown";
import { useFormsConfig } from "../../Context";
import CustomConditionControl from "../CustomConditionControl";
import * as Yup from 'yup';
import { formConditionValidation, conditionKeys, removeTouchedProps } from "../ItemValidation";
import HeaderTabs from "../../controls/HeaderTabs";
import ExperienceModes from "../../../../guest/ExperienceModes";

const nameRegex = /^[a-zA-Z0-9][a-zA-Z0-9_\s]*[a-zA-Z0-9]$/;

const EditItem = ({
    formTemplateId,
    isEdit,
    isShared,
    defaultShowSave = false,
    item = {},
    section = {},
    show,
    allowUpdate,
    onAdd,
    onUpdate,
    onClose
}) => {
    const { id: deploymentId } = useParams();

    const [translate] = useTranslation();
    const { formTemplate, templatePreferences, dataTypes, dataLists, logAmplitudeEvent, setExperienceMode, modalContainerRef,
        LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS } = useFormsConfig();

    const [showSaveButton, setShowSaveButton] = useState(defaultShowSave);
    const [selectedTab, setSelectedTab] = useState("item");

    useEffect(() => {
        if (show) {
            setExperienceMode(isEdit ? ExperienceModes.EDIT_ITEM : ExperienceModes.ADD_ITEM);
        }
    }, [show, isEdit, setExperienceMode]);

    const getConfigList = useCallback((dataType) => {
        const result = [
            { id: "btn_tab_item", _id: "item", displayValue: translate('OTM_ITEM') },
            { id: "btn_tab_visible", _id: "visible", displayValue: translate('OTM_VISIBLE') },
        ];

        if (dataType !== 'no-input') {
            result.push(
                { id: "btn_tab_readOnly", _id: "readOnly", displayValue: translate('OTM_READONLY') },
                { id: "btn_tab_validation", _id: "validation", displayValue: translate('OTM_VALIDATION') },
            );
        }

        return result;
    }, [translate]);

    const handleSubmit = useCallback(async (values) => {
        setShowSaveButton(false);

        removeTouchedProps(["visible", "readOnly", "validation"], values);

        if (isEdit) {
            let response;
            if (isShared) {
                response = await Forms.updateSharedSectionItem({
                    deploymentId,
                    sharedSectionId: section._id,
                    sharedSectionItemId: item._id,
                    body: values
                });
            }
            else {
                response = await Forms.editItem({
                    deploymentId,
                    formTemplateId,
                    sectionId: section._id,
                    itemId: item._id,
                    body: values
                });

                logAmplitudeEvent({
                    eventType: "updatedItem",
                    eventProperties: {
                        deploymentId,
                        formTemplateId,
                        sectionName: section.name,
                        itemName: values.name,
                    }
                });
            }

            if (!response.error) {
                await onUpdate();
            }
        }
        else {
            let response;
            if (isShared) {
                response = await Forms.createSharedSectionItem({
                    deploymentId,
                    sharedSectionId: section._id,
                    body: {
                        ...values,
                        order: item.order
                    }
                });
            }
            else {
                response = await Forms.addItem({
                    deploymentId,
                    formTemplateId,
                    sectionId: section._id,
                    body: {
                        ...values,
                        order: item.order
                    }
                });
                logAmplitudeEvent({
                    eventType: "addedItem",
                    eventProperties: {
                        deploymentId,
                        formTemplateId,
                        sectionName: section.name,
                        itemName: values.name,
                    }
                });
            }

            if (!response.error && response.data) {
                await onAdd(response.data);
            }
        }
        setExperienceMode(ExperienceModes.HOME_PAGE);
    }, [isEdit, isShared, deploymentId, formTemplateId, section._id, section.name, item._id, item.order, onAdd, onUpdate, logAmplitudeEvent, setExperienceMode]);

    const handleClose = useCallback(() => {
        onClose();
        setExperienceMode(ExperienceModes.HOME_PAGE);
    }, [onClose, setExperienceMode]);

    const handleOnChange = useCallback(() => {
        setShowSaveButton(true);
    }, []);

    // TODO [OTM-6566]: figure out how to allow an empty array in Yup for condition validation (remove if checks)
    const getErrorList = useCallback((props) => {
        const errors = {};
        for (const fieldName in props.errors || {})
            switch (fieldName) {
                case "visible":
                    if (props.values.visible.length > 0) {
                        errors.visible = true;
                    }
                    break;
                case "readOnly":
                    if (props.values.readOnly.length > 0) {
                        errors.readOnly = true;
                    }
                    break;
                case "validation":
                    if (props.values.validation.length > 0) {
                        errors.validation = true;
                    }
                    break;
                default:
                    errors.item = true;
                    break;
            }

        return errors;
    }, []);

    const renderHeader = useCallback((props) => {
        return <div className="row flex-grow-1 mt-n2 align-items-center justify-content-end">
            <div className="col-12 col-lg mt-2">
                <label className="h5">{isEdit ? translate("OTM_EDIT_ITEM") : translate("OTM_ADD_ITEM")}</label>
            </div>
            <HeaderTabs
                listData={getConfigList(props.values.dataType)}
                listAttr="displayValue"
                selectedTab={selectedTab}
                listOnClick={(_id) => setSelectedTab(_id)}
                errors={getErrorList(props)}
            />
        </div>
    }, [getConfigList, selectedTab, isEdit, getErrorList, translate]);

    const renderItemSection = useCallback((props) => {
        const dataTypesOptions = dataTypes ? [{ _id: 'no-input', name: 'label-only' }, ...dataTypes.map(o => ({ _id: o, name: o }))] : [{ _id: 'no-input', name: 'label-only' }];

        return <>
            <div className="row mt-n3 align-items-start">
                <div id="field_item_name" className="col-12 mt-3" >
                    <TextBox
                        label={translate("OTM_ITEM_NAME")}
                        type="text"
                        name="name"
                        disabled={!allowUpdate}
                        isRequired={true}
                        onChange={(event) => {
                            if (props.initialValues['labelValue'] === '') {
                                props.setFieldValue('labelValue', event.target.value);
                            }
                            props.setFieldValue('name', event.target.value);
                        }}
                    />
                </div>
                <div id="field_item_dataType" className={`col-12 mt-3 col-lg-6`}>
                    <Dropdown
                        heading={translate("OTM_DATA_TYPE")}
                        name="dataType"
                        readOnly={!allowUpdate}
                        isRequired={true}
                        onSelect={(value) => {
                            if (value === 'no-input') {
                                props.setFieldValue('labelBolded', templatePreferences.itemTextBolded ?? false);
                                props.setFieldValue('labelItalic', templatePreferences.itemTextItalic ?? false);
                                props.setFieldValue('labelUnderline', templatePreferences.itemTextUnderline ?? false);
                                props.setFieldValue('labelFontSize', templatePreferences.itemTextFontSize ?? 1);
                                props.setFieldValue('labelTypeGroup', 'label-only');
                                props.setFieldValue('isRequired', false);
                            }
                            else {
                                props.setFieldValue('labelBolded', templatePreferences.itemLabelBolded ?? true);
                                props.setFieldValue('labelItalic', templatePreferences.itemLabelItalic ?? false);
                                props.setFieldValue('labelUnderline', templatePreferences.itemLabelUnderline ?? false);
                                props.setFieldValue('labelFontSize', templatePreferences.itemLabelFontSize ?? 1);
                                props.setFieldValue('labelTypeGroup', 'label-and-input');
                            }

                            handleOnChange();
                        }}
                        options={dataTypesOptions}
                        grow={true}
                    />
                </div>

                {props.values["dataType"] === 'photo' && <>
                    <div className={`col-12 mt-3 d-flex align-items-end col-lg-2`}>
                        <CheckBox
                            buttonTooltip={true}
                            tooltip={translate("OTM_ALLOW_MULTIPLE_PHOTO_TOOLTIP")}
                            label={translate("OTM_ALLOW_MULTIPLE")}
                            name="multipleOptions"
                            disabled={!allowUpdate}
                        />
                    </div>
                </>}

                {props.values["dataType"] === 'select-option' && <>
                    <div id="field_item_dataList" className={`col-12 mt-3 d-flex align-items-end col-lg-6`}>
                        <div className="flex-grow-1">
                            <Dropdown grow={true} heading={translate("OTM_DATA_LIST")} name="dataList" readOnly={!allowUpdate} isRequired={true} onSelect={handleOnChange} options={[{ _id: null, name: '--Select--' }, ...dataLists]} />
                        </div>
                    </div>
                    <div id="field_item_allowMultiple" className={`col-12 mt-3 d-flex align-items-end col-lg-3 col-xl-2`}>
                        <CheckBox
                            buttonTooltip={true}
                            tooltip={translate("OTM_ALLOW_MULTIPLE_TOOLTIP")}
                            label={translate("OTM_ALLOW_MULTIPLE")}
                            name="multipleOptions"
                            disabled={!allowUpdate}
                        />
                    </div>
                    <div id="field_item_selectType" className={`col-12 mt-3 d-flex align-items-end col-lg-3 col-xl-2`}>
                        <div className="flex-grow-1">
                            <Dropdown
                                grow={true}
                                heading={translate("OTM_OPTION_SELECT_TYPE")}
                                name="optionSelectType"
                                readOnly={!allowUpdate}
                                isRequired={true}
                                onSelect={handleOnChange}
                                options={[{ _id: 'select', name: 'Buttons Select' }, { _id: 'dropdown', name: 'Dropdown' }]} />
                        </div>
                    </div>
                    {props.values["optionSelectType"] === 'dropdown' && <div className={`col-12 mt-3 d-flex align-items-end col-lg-3 col-xl-2`}>
                        <div id="field_item_allowSearch" className="flex-grow-1">
                            <CheckBox
                                buttonTooltip={true}
                                tooltip={translate("OTM_DROPDOWN_ALLOW_SEARCH_TOOLTIP")}
                                label={translate("OTM_DROPDOWN_ALLOW_SEARCH")}
                                name="searchableDropdown"
                                disabled={!allowUpdate}
                            />
                        </div>
                    </div>}
                </>}
                {["single-line-text", "multi-line-text"].includes(props.values["dataType"]) && <>
                    <div id="field_item_name" className="col-4 mt-3" >
                        <TextBox
                            label={translate("OTM_MAX_LENGTH")}
                            type="number"
                            name="maxLength"
                            disabled={!allowUpdate}
                        />
                    </div>
                </>}

                {props.values["dataType"] === 'number' && <>
                    <div id="field_item_numberAllowedDecimals" className={`col-12 col-sm-6 mt-3 col-lg-3`}>
                        <Dropdown grow={true} heading={translate("OTM_NUMBER_ALLOWED_DECIMALS")} name="numberAllowedDecimals" readOnly={!allowUpdate} onSelect={handleOnChange} options={DECIMALS_OPTIONS} />
                    </div>
                    <div id="field_item_numberShowStepButtons" className={`col-12 col-sm-6 mt-3 col-lg-3`}>
                        <CheckBox label={translate("OTM_NUMBER_SHOW_STEP_BUTTONS")} name="numberShowStepButtons" disabled={!allowUpdate} />
                    </div>
                </>}

                <div className="col-12"></div>
                <div id="field_item_labelValue" className={`col-12 col-lg mt-3`}>
                    <TextBox label={translate("OTM_LABEL_VALUE")} type="text" name="labelValue" disabled={!allowUpdate} />
                </div>
                <div id="field_item_labelBolded" className={`col-auto mt-3`}>
                    <CheckBox
                        label={translate("OTM_LABEL_BOLDED")}
                        disabled={!allowUpdate}
                        onChange={handleOnChange}
                        name="labelBolded" />
                </div>
                <div id="field_item_labelItalic" className={`col-auto mt-3`}>
                    <CheckBox
                        label={translate("OTM_LABEL_ITALIC")}
                        disabled={!allowUpdate}
                        onChange={handleOnChange}
                        name="labelItalic" />
                </div>
                <div id="field_item_labelUnderline" className={`col-auto mt-3`}>
                    <CheckBox
                        label={translate("OTM_LABEL_UNDERLINE")}
                        disabled={!allowUpdate}
                        onChange={handleOnChange}
                        name="labelUnderline" />
                </div>
                <div id="field_item_labelFontSize" className={`col-auto mt-3`}>
                    <Dropdown
                        tooltip={translate("OTM_LABEL_FONT_SIZE_TOOLTIP")}
                        buttonTooltip={true}
                        grow={true}
                        heading={translate('OTM_LABEL_FONT_SIZE')}
                        name="labelFontSize"
                        readOnly={!allowUpdate}
                        onSelect={handleOnChange}
                        options={LABEL_FONT_SIZE_OPTIONS}
                    />
                </div>

                <div className="col-12"></div>

                {props.values["dataType"] !== "no-input" ? <div id="field_item_helpText" className={`col-12 col-lg mt-3`}>
                    <TextBox label={translate("OTM_HELP_TEXT")} type="text" name="helpText" disabled={!allowUpdate} />
                </div> : null}

                <div className="col-12"></div>

                <div id="field_item_columnSpanSmall" className={`col-12 col-sm-6 mt-3`}>
                    <Dropdown
                        tooltip={translate("OTM_COLUMNS_SPAN_SMALL_TOOLTIP")}
                        buttonTooltip={true}
                        grow={true}
                        heading={translate("OTM_COLUMNS_SPAN_SMALL")}
                        name="columnSpanSmall"
                        readOnly={!allowUpdate}
                        onSelect={handleOnChange}
                        options={COLUMNS_OPTIONS} />
                </div>
                <div id="field_item_columnSpanLarge" className={`col-12 col-sm-6 mt-3`}>
                    <Dropdown
                        tooltip={translate("OTM_COLUMNS_SPAN_LARGE_TOOLTIP")}
                        buttonTooltip={true}
                        grow={true}
                        heading={translate("OTM_COLUMNS_SPAN_LARGE")}
                        name="columnSpanLarge"
                        readOnly={!allowUpdate}
                        onSelect={handleOnChange}
                        options={COLUMNS_OPTIONS} />
                </div>
                <div className="col-12">
                </div>
                {props.values["dataType"] !== 'no-input' && <>
                    <div id="field_item_isRequired" className={`col-12 col-sm-6 mt-3 ${modalContainerRef ? "" : "col-xl-3"}`}>
                        <CheckBox
                            label={translate("OTM_REQUIRED")}
                            disabled={!allowUpdate}
                            onChange={(value) => {
                                if (value === true) {
                                    props.setFieldValue('isReadOnly', false);
                                }
                            }}
                            name="isRequired" />
                    </div>
                    <div id="field_item_isReadOnly" className={`col-12 col-sm-6 mt-3 ${modalContainerRef ? "" : "col-xl-3"}`}>
                        <CheckBox
                            label={translate("OTM_READONLY")}
                            disabled={!allowUpdate}
                            onChange={(value) => {
                                if (value === true) {
                                    props.setFieldValue('isRequired', false);
                                }
                            }}
                            name="isReadOnly" />
                    </div>
                    <div id="field_item_showStylesWhenValid" className={`col-12 col-sm-6 mt-3 ${modalContainerRef ? "" : "col-xl-3"}`}>
                        <CheckBox
                            tooltip={translate("OTM_SHOW_STYLES_WHEN_VALID_TOOLTIP")}
                            buttonTooltip={true}
                            label={translate("OTM_SHOW_STYLES_WHEN_VALID")}
                            name="showStylesWhenValid"
                            disabled={!allowUpdate} />
                    </div>
                    <div id="field_item_showStylesWhenInvalid" className={`col-12 col-sm-6 mt-3 ${modalContainerRef ? "" : "col-xl-3"}`}>
                        <CheckBox
                            tooltip={translate("OTM_SHOW_STYLES_WHEN_INVALID_TOOLTIP")}
                            buttonTooltip={true}
                            label={translate("OTM_SHOW_STYLES_WHEN_INVALID")}
                            disabled={!allowUpdate}
                            name="showStylesWhenInvalid" />
                        {props.values["showStylesWhenInvalid"] === false && formTemplate.formType === 'plain' && <span className='text-accent fs-7 float-start'>
                            {translate("OTM_ITEM_HAS_NO_ENABLED_INVALID_MESSAGES")}
                        </span>}
                    </div>
                </>}

            </div>
        </>
    }, [allowUpdate, dataTypes, dataLists, formTemplate, templatePreferences, handleOnChange, modalContainerRef, LABEL_FONT_SIZE_OPTIONS, COLUMNS_OPTIONS, DECIMALS_OPTIONS, translate]);

    const renderSection = useCallback((props) => {
        switch (selectedTab) {
            case 'visible': {
                return <CustomConditionControl
                    label={"OTM_VISIBLE_WHEN"}
                    labelNoStatements={"OTM_VISIBLE_ALWAYS"}
                    name="visible"
                    onChange={handleOnChange}
                    disabled={!allowUpdate}
                    item={{
                        ...item,
                        ...props.values
                    }}
                    isForItem={true}
                    addNewItem={!isEdit}
                    sectionId={section?._id}
                    conditionKeys={conditionKeys} />
            }
            case 'readOnly': {
                return <CustomConditionControl
                    label={"OTM_READONLY_WHEN"}
                    labelNoStatements={"OTM_READONLY_ALWAYS"}
                    name="readOnly"
                    onChange={handleOnChange}
                    disabled={!allowUpdate}
                    item={{
                        ...item,
                        ...props.values
                    }}
                    isForItem={true}
                    addNewItem={!isEdit}
                    sectionId={section?._id}
                    conditionKeys={conditionKeys}
                    itemReadOnly={props?.values?.isReadOnly || false} />
            }
            case 'validation': {
                return <CustomConditionControl
                    label={"OTM_VALID_WHEN"}
                    labelNoStatements={"OTM_VALID_ALWAYS"}
                    name="validation"
                    onChange={handleOnChange}
                    disabled={!allowUpdate}
                    item={{
                        ...item,
                        ...props.values
                    }}
                    isForItem={true}
                    addNewItem={!isEdit}
                    sectionId={section?._id}
                    conditionKeys={[...conditionKeys, 'message']} />
            }
            case 'item':
            default: {
                return renderItemSection(props);
            }
        }
    }, [selectedTab, allowUpdate, item, section, isEdit, renderItemSection, handleOnChange]);

    // TODO [OTM-6566]: figure out how to allow an empty array in Yup for condition validation
    const formIsValid = (props) => {
        const manualErrorCheck = { ...props.errors };

        let conditionsOverride = false;

        ['visible', 'readOnly', 'validation'].forEach((key) => {
            if (props.values[key] && props.values[key].length === 0) {
                conditionsOverride = true;
                delete manualErrorCheck[key];
            }
        });

        return conditionsOverride ? Object.keys(manualErrorCheck).length === 0 : props.isValid;
    };

    const renderForm = useMemo(() => {
        const defaultDatatype = item?.dataType ?? templatePreferences.itemDataType ?? "";
        let labelBolded = item?.labelBolded;
        if (!labelBolded && defaultDatatype) {
            labelBolded = defaultDatatype === "no-input" ? (templatePreferences.itemTextBolded ?? false) : (templatePreferences.itemLabelBolded ?? true);
        }
        let labelItalic = item?.labelItalic;
        if (!labelItalic && defaultDatatype) {
            labelItalic = defaultDatatype === "no-input" ? (templatePreferences.itemTextItalic ?? false) : (templatePreferences.itemLabelItalic ?? false);
        }
        let labelUnderline = item?.labelUnderline;
        if (!labelUnderline && defaultDatatype) {
            labelUnderline = defaultDatatype === "no-input" ? (templatePreferences.itemTextUnderline ?? false) : (templatePreferences.itemLabelUnderline ?? false);
        }

        let labelFontSize = item?.labelFontSize;
        if (!labelFontSize && defaultDatatype) {
            labelFontSize = defaultDatatype === "no-input" ? (templatePreferences.itemTextFontSize ?? 1) : (templatePreferences.itemLabelFontSize ?? 1);
        }

        return <Formik initialValues={{
            _id: item?._id ?? undefined,
            name: item?.name ?? "",
            isRequired: item?.isRequired ?? templatePreferences.itemIsRequired ?? false,
            isReadOnly: item?.isReadOnly ?? templatePreferences.itemIsReadOnly ?? false,
            showStylesWhenValid: item?.showStylesWhenValid ?? templatePreferences.itemShowStylesWhenValid ?? true,
            showStylesWhenInvalid: item?.showStylesWhenInvalid ?? templatePreferences.itemShowStylesWhenInvalid ?? true,
            columnSpanSmall: item?.columnSpanSmall ?? templatePreferences.itemColumnSpanSmall ?? 12,
            columnSpanLarge: item?.columnSpanLarge ?? templatePreferences.itemColumnSpanLarge ?? 12,
            labelTypeGroup: item?.labelTypeGroup ?? "",
            labelBolded: labelBolded ?? false,
            labelItalic: labelItalic ?? false,
            labelUnderline: labelUnderline ?? false,
            labelFontSize: labelFontSize ?? 1,
            labelValue: item?.labelValue ?? "",
            helpText: item?.helpText ?? "",
            dataType: defaultDatatype,
            visible: item?.visible ?? [],
            readOnly: item?.readOnly ?? [],
            validation: item?.validation ?? [],
            dataList: item?.dataList?._id ?? null,
            multipleOptions: item?.multipleOptions ?? false,
            optionSelectType: item?.optionSelectType ?? "select",
            searchableDropdown: item?.searchableDropdown ?? true,
            numberAllowedDecimals: item?.numberAllowedDecimals ?? templatePreferences.itemNumberAllowedDecimals ?? 0,
            numberShowStepButtons: item?.numberShowStepButtons ?? templatePreferences.itemNumberShowStepButtons ?? true,
            maxLength: isEdit ? item?.maxLength : item?.maxLength ?? templatePreferences.itemMaxLength,
        }}
            validateOnMount
            validationSchema={Yup.object({
                name: Yup.string()
                    .matches(nameRegex, translate("OTM_INVALID_ITEM_NAME"))
                    .test("unique-name",
                        translate("OTM_ITEM_NAME_ALREAD_EXISTS"),
                        (value) => { return !section?.items.find(o => o.name === value && o._id !== item._id) })
                    .required(translate("OTM_ITEM_NAME_REQUIRED")),
                visible: formConditionValidation(translate),
                readOnly: formConditionValidation(translate),
                validation: formConditionValidation(translate, true),
                dataType: Yup.string().nullable().required(translate("OTM_DATA_TYPE_REQUIRED")),
                dataList: Yup.string().nullable().when("dataType", (dataType) => {
                    if (dataType === 'select-option') {
                        return Yup.string().nullable().required(translate("OTM_DATA_LIST_REQUIRED"));
                    }
                }),
                maxLength: Yup.number().min(0, translate("OTM_MAX_LENGTH_MUST_BE_ABOVE_0")).max(9999, translate("OTM_MAX_LENGTH_MUST_BE_BELOW_9999")).integer(translate("OTM_MAX_LENGTH_MUST_BE_INTEGER")).nullable()
            })}
            onSubmit={handleSubmit} enableReinitialize>
            {props => (
                <Form onChange={handleOnChange} className="flex-grow-1 d-flex flex-column">
                    <Modal.Header>
                        {renderHeader(props)}
                    </Modal.Header>

                    <Modal.Body className={``}>
                        {renderSection(props)}
                    </Modal.Body>

                    <Modal.Footer>
                        {showSaveButton && allowUpdate && formIsValid(props) && <Button id="btn_item_save" size='sm' type="submit" variant='primary'>{translate('OTM_SAVE')}</Button>}
                        <Button size='sm' variant='secondary' onClick={handleClose}>{translate(allowUpdate ? 'OTM_CANCEL' : 'OTM_CLOSE')} </Button>
                    </Modal.Footer>
                </Form>
            )}
        </Formik>
    }, [showSaveButton, section, item, isEdit, templatePreferences, allowUpdate, handleClose, renderHeader, renderSection, handleOnChange, handleSubmit, translate]);

    return <Modal
        container={modalContainerRef ? modalContainerRef.current : null}
        size={modalContainerRef ? 'lg' : 'xl'}
        show={show}
        backdrop={'static'}>
        {renderForm}
    </Modal>
}

export default EditItem;